import { useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormGroup } from "../../components/FormGroup";
import { FormLabel } from "../../components/FormLabel";
import { Form } from "../../components/Form";
import { Input } from "../../components/Input";
import { SelectInput } from "../../components/SelectInput";
import { Toggle } from "../../components/Toggle";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { SerialScaleUnit } from "@KassaroAB/shared/types/enums/serialScaleUnit";
import type { RestCreateUnit, RestUnit } from "@KassaroAB/shared/types/unit";
import { FormSection } from "../../components/FormSection";

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const Example = styled.p`
  margin: 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const DECIMAL_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

interface UnitFormProps {
  formId: string;
  unit?: RestUnit;
  savedTick?: number;
  onSubmit: (data: RestCreateUnit) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const UnitForm = ({
  formId,
  unit,
  savedTick,
  onSubmit,
  onDirtyChange,
}: UnitFormProps) => {
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      name: unit?.name ?? "",
      suffix: unit?.suffix ?? "",
      decimals: unit?.decimals ?? 0,
      enableSerialScale: unit?.enableSerialScale ?? false,
      serialScaleUnit: unit?.serialScaleUnit ?? SerialScaleUnit.KILOGRAM,
    },
    onSubmit: ({ value }) => {
      onSubmit({
        name: value.name,
        suffix: value.suffix,
        decimals: value.decimals,
        enableSerialScale: value.enableSerialScale,
        serialScaleUnit: value.serialScaleUnit,
      });
    },
  });

  const isDirty = useStore(form.store, (state) => !state.isDefaultValue);

  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!savedTick) return;
    form.reset(form.state.values);
  }, [savedTick, form]);

  return (
    <Form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <FormSection title={t("unitForm.basicInformation")}>
        <Fields>
          <form.Field name="name">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("unitForm.name")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="suffix">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("unitForm.suffix")}</FormLabel>
                <Input
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t("unitForm.suffixPlaceholder")}
                  required
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Field name="decimals">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("unitForm.decimals")}</FormLabel>
                <SelectInput
                  value={String(field.state.value)}
                  onChange={(e) => field.handleChange(Number(e.target.value))}
                  onBlur={field.handleBlur}
                >
                  {DECIMAL_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </SelectInput>
              </FormGroup>
            )}
          </form.Field>
          <form.Subscribe
            selector={(state) => ({
              decimals: state.values.decimals,
              suffix: state.values.suffix,
            })}
          >
            {(selected) => (
              <Example>
                {t("unitForm.example")} {123.456789.toFixed(selected.decimals)}
                {selected.suffix ? ` ${selected.suffix}` : ""}
              </Example>
            )}
          </form.Subscribe>
        </Fields>
      </FormSection>

      <FormSection title={t("unitForm.serialScale")}>
        <Fields>
          <form.Field name="enableSerialScale">
            {(field) => (
              <FormGroup>
                <FormLabel>{t("unitForm.enableSerialScale")}</FormLabel>
                <Toggle
                  checked={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                />
              </FormGroup>
            )}
          </form.Field>
          <form.Subscribe selector={(state) => state.values.enableSerialScale}>
            {(enableSerialScale) =>
              enableSerialScale ? (
                <form.Field name="serialScaleUnit">
                  {(field) => (
                    <FormGroup>
                      <FormLabel>{t("unitForm.serialScaleUnit")}</FormLabel>
                      <SelectInput
                        value={field.state.value}
                        onChange={(e) =>
                          field.handleChange(e.target.value as SerialScaleUnit)
                        }
                        onBlur={field.handleBlur}
                      >
                        {Object.values(SerialScaleUnit).map((scaleUnit) => (
                          <option key={scaleUnit} value={scaleUnit}>
                            {t(`unitForm.serialScaleUnit${scaleUnit}`)}
                          </option>
                        ))}
                      </SelectInput>
                    </FormGroup>
                  )}
                </form.Field>
              ) : null
            }
          </form.Subscribe>
        </Fields>
      </FormSection>
    </Form>
  );
};
